import { useRef, useEffect, useState, useMemo } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';

// Enhanced AI Quantum Computing Core - More eye-catching with 3 depth layers
export const QuantumCore = ({ mouseX, mouseY }) => {
  // Layer 1: Far - Cosmic Data Streams (flowing data particles with trails)
  const cosmicParticles = useMemo(() => {
    const particles = [];
    for (let i = 0; i < 80; i++) {
      particles.push({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 3 + 1,
        speed: Math.random() * 15 + 8,
        delay: Math.random() * 5,
        opacity: Math.random() * 0.5 + 0.2,
        color: i % 3 === 0 ? '#00D4FF' : i % 3 === 1 ? '#8B5CF6' : '#0EA5E9',
      });
    }
    return particles;
  }, []);

  // Layer 2: Middle - Neural Network with glowing connections
  const neuralNodes = useMemo(() => {
    const nodes = [];
    const centerX = 50;
    const centerY = 50;
    const rings = 5;
    const nodesPerRing = [1, 6, 12, 18, 24];
    
    let nodeId = 0;
    for (let ring = 0; ring < rings; ring++) {
      const radius = ring === 0 ? 0 : (ring * 18);
      const count = nodesPerRing[ring];
      for (let i = 0; i < count; i++) {
        const angle = (2 * Math.PI * i) / count + (ring * 0.15);
        nodes.push({
          id: nodeId++,
          x: centerX + radius * Math.cos(angle),
          y: centerY + radius * Math.sin(angle),
          ring,
          size: ring === 0 ? 20 : 10 - ring,
          pulseDelay: ring * 0.15 + i * 0.03,
          color: ring === 0 ? '#00D4FF' : ring % 2 === 0 ? '#8B5CF6' : '#00D4FF',
        });
      }
    }
    return nodes;
  }, []);

  // Layer 3: Near - Floating Holographic UI Cards
  const holoElements = useMemo(() => {
    return [
      { id: 0, x: 8, y: 15, width: 100, height: 60, type: 'card', icon: 'chart' },
      { id: 1, x: 78, y: 20, width: 80, height: 45, type: 'card', icon: 'data' },
      { id: 2, x: 12, y: 65, width: 90, height: 50, type: 'card', icon: 'flow' },
      { id: 3, x: 72, y: 70, width: 75, height: 40, type: 'card', icon: 'metric' },
      { id: 4, x: 42, y: 40, width: 40, height: 40, type: 'core', icon: 'ai' },
    ];
  }, []);

  return (
    <div className="relative w-full h-full" style={{ perspective: '2000px' }}>
      {/* Layer 1: Cosmic Data Streams (Deepest) */}
      <motion.div 
        className="absolute inset-0 overflow-hidden"
        style={{ 
          x: useTransform(mouseX, [-0.5, 0.5], [30, -30]),
          y: useTransform(mouseY, [-0.5, 0.5], [30, -30]),
        }}
      >
        {/* Vertical data streams */}
        {[...Array(12)].map((_, i) => (
          <motion.div
            key={`stream-${i}`}
            className="absolute"
            style={{
              left: `${8 + i * 8}%`,
              top: '100%',
              width: '2px',
              height: '50%',
              background: `linear-gradient(to top, transparent, ${i % 2 === 0 ? 'rgba(0, 212, 255, 0.4)' : 'rgba(139, 92, 246, 0.3)'}, transparent)`,
            }}
            animate={{
              y: [0, '-200%'],
              opacity: [0, 0.6, 0],
            }}
            transition={{
              duration: 3 + i * 0.3,
              repeat: Infinity,
              delay: i * 0.5,
              ease: 'linear',
            }}
          />
        ))}

        {/* Floating particles */}
        {cosmicParticles.map((particle) => (
          <motion.div
            key={particle.id}
            className="absolute rounded-full"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: particle.size,
              height: particle.size,
              background: `radial-gradient(circle, ${particle.color} 0%, transparent 70%)`,
              boxShadow: `0 0 ${particle.size * 4}px ${particle.color}50`,
            }}
            animate={{
              y: [0, -150, -300],
              x: [0, Math.sin(particle.id) * 30, 0],
              opacity: [0, particle.opacity, 0],
              scale: [0.5, 1.2, 0.3],
            }}
            transition={{
              duration: particle.speed,
              repeat: Infinity,
              delay: particle.delay,
              ease: 'linear',
            }}
          />
        ))}

        {/* Horizontal scanning lines */}
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={`scan-${i}`}
            className="absolute left-0 right-0 h-px"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.5), rgba(139, 92, 246, 0.3), transparent)',
            }}
            animate={{
              top: ['0%', '100%'],
              opacity: [0, 0.5, 0],
            }}
            transition={{
              duration: 4 + i,
              repeat: Infinity,
              delay: i * 1.5,
              ease: 'linear',
            }}
          />
        ))}
      </motion.div>

      {/* Layer 2: Neural Network (Middle) */}
      <motion.div 
        className="absolute inset-0"
        style={{ 
          x: useTransform(mouseX, [-0.5, 0.5], [15, -15]),
          y: useTransform(mouseY, [-0.5, 0.5], [15, -15]),
        }}
      >
        {/* Neural connections */}
        <svg className="absolute inset-0 w-full h-full" style={{ opacity: 0.2 }}>
          <defs>
            <linearGradient id="neuralGradientNew" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#00D4FF" stopOpacity="0.8" />
              <stop offset="50%" stopColor="#8B5CF6" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#00D4FF" stopOpacity="0.8" />
            </linearGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          {neuralNodes.map((node, i) => 
            neuralNodes.slice(i + 1).filter(n => {
              const dist = Math.sqrt(Math.pow(n.x - node.x, 2) + Math.pow(n.y - node.y, 2));
              return dist < 25 && Math.abs(n.ring - node.ring) <= 1;
            }).map((target, j) => (
              <motion.line
                key={`conn-${i}-${j}`}
                x1={`${node.x}%`}
                y1={`${node.y}%`}
                x2={`${target.x}%`}
                y2={`${target.y}%`}
                stroke="url(#neuralGradientNew)"
                strokeWidth="1.5"
                filter="url(#glow)"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 0.6 }}
                transition={{ duration: 1.5, delay: node.pulseDelay }}
              />
            ))
          )}
        </svg>

        {/* Neural nodes */}
        {neuralNodes.map((node) => (
          <motion.div
            key={node.id}
            className="absolute rounded-full"
            style={{
              left: `${node.x}%`,
              top: `${node.y}%`,
              width: node.size,
              height: node.size,
              transform: 'translate(-50%, -50%)',
              background: node.ring === 0 
                ? `radial-gradient(circle, ${node.color} 0%, rgba(0, 212, 255, 0.5) 50%, transparent 100%)`
                : `radial-gradient(circle, ${node.color}CC 0%, transparent 100%)`,
              boxShadow: node.ring === 0 
                ? `0 0 60px ${node.color}80, 0 0 100px ${node.color}40`
                : `0 0 ${node.size * 2}px ${node.color}60`,
            }}
            animate={{
              scale: [1, 1.4, 1],
              opacity: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 2 + node.ring * 0.4,
              repeat: Infinity,
              delay: node.pulseDelay,
              ease: 'easeInOut',
            }}
          />
        ))}

        {/* Central pulse rings */}
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={`pulse-${i}`}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border"
            style={{
              borderColor: i % 2 === 0 ? 'rgba(0, 212, 255, 0.3)' : 'rgba(139, 92, 246, 0.2)',
            }}
            animate={{
              width: [0, 300],
              height: [0, 300],
              opacity: [0.6, 0],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              delay: i * 1,
              ease: 'easeOut',
            }}
          />
        ))}
      </motion.div>

      {/* Layer 3: Holographic UI Elements (Nearest) */}
      <motion.div 
        className="absolute inset-0"
        style={{ 
          x: useTransform(mouseX, [-0.5, 0.5], [-20, 20]),
          y: useTransform(mouseY, [-0.5, 0.5], [-20, 20]),
        }}
      >
        {holoElements.map((el, i) => (
          <motion.div
            key={el.id}
            className="absolute"
            style={{
              left: `${el.x}%`,
              top: `${el.y}%`,
              width: el.width,
              height: el.height,
            }}
            initial={{ opacity: 0, scale: 0.8, rotateX: 20 }}
            animate={{ 
              opacity: [0.15, 0.35, 0.15],
              scale: [0.95, 1.02, 0.95],
              y: [0, -8, 0],
              rotateX: [5, -5, 5],
            }}
            transition={{
              duration: 5 + i,
              repeat: Infinity,
              delay: i * 0.7,
              ease: 'easeInOut',
            }}
          >
            {el.type === 'card' ? (
              <div 
                className="w-full h-full rounded-xl overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(139, 92, 246, 0.05) 50%, rgba(0, 212, 255, 0.08) 100%)',
                  border: '1px solid rgba(0, 212, 255, 0.2)',
                  backdropFilter: 'blur(8px)',
                  boxShadow: '0 0 30px rgba(0, 212, 255, 0.1)',
                }}
              >
                {/* Fake UI content */}
                <div className="p-3 space-y-2">
                  <div className="h-1.5 w-3/4 rounded-full bg-gradient-to-r from-cyan-500/40 to-purple-500/20" />
                  <div className="h-1 w-1/2 rounded-full bg-gradient-to-r from-cyan-500/30 to-transparent" />
                  <div className="h-1 w-2/3 rounded-full bg-gradient-to-r from-cyan-500/20 to-transparent" />
                  {el.icon === 'chart' && (
                    <div className="flex items-end gap-1 h-6 mt-2">
                      {[40, 65, 45, 80, 55, 70].map((h, idx) => (
                        <motion.div
                          key={idx}
                          className="flex-1 rounded-t bg-gradient-to-t from-cyan-500/50 to-purple-500/30"
                          style={{ height: `${h}%` }}
                          animate={{ height: [`${h}%`, `${h + 20}%`, `${h}%`] }}
                          transition={{ duration: 2, repeat: Infinity, delay: idx * 0.1 }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <motion.div 
                className="w-full h-full rounded-full flex items-center justify-center"
                style={{
                  background: 'radial-gradient(circle, rgba(139, 92, 246, 0.2) 0%, rgba(0, 212, 255, 0.1) 50%, transparent 70%)',
                  border: '2px solid rgba(139, 92, 246, 0.3)',
                  boxShadow: '0 0 50px rgba(139, 92, 246, 0.2), inset 0 0 30px rgba(0, 212, 255, 0.1)',
                }}
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
              >
                <div className="w-3/4 h-3/4 rounded-full border border-cyan-500/30 flex items-center justify-center">
                  <div className="w-1/2 h-1/2 rounded-full bg-gradient-to-br from-cyan-500/40 to-purple-500/40" />
                </div>
              </motion.div>
            )}
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
};

// Simpler Globe for hero section
export const Globe3D = ({ mouseX, mouseY }) => {
  const spheres = useMemo(() => {
    const points = [];
    const numRings = 10;
    const spheresPerRing = 12;
    
    for (let ring = 0; ring < numRings; ring++) {
      const phi = (Math.PI * ring) / (numRings - 1);
      const y = Math.cos(phi);
      const ringRadius = Math.sin(phi);
      const numInRing = Math.max(1, Math.round(spheresPerRing * ringRadius));
      
      for (let i = 0; i < numInRing; i++) {
        const theta = (2 * Math.PI * i) / numInRing;
        const x = ringRadius * Math.cos(theta);
        const z = ringRadius * Math.sin(theta);
        
        points.push({
          x: x * 110,
          y: y * 110,
          z: z * 110,
          delay: (ring * 0.08) + (i * 0.015),
          size: 5 + Math.random() * 3,
          brightness: 0.4 + Math.random() * 0.6,
          color: ring % 2 === 0 ? '#00D4FF' : '#8B5CF6',
        });
      }
    }
    return points;
  }, []);

  // Orbit rings
  const orbitRings = useMemo(() => [
    { size: 280, duration: 25, delay: 0, opacity: 0.15 },
    { size: 320, duration: 30, delay: 2, opacity: 0.1 },
    { size: 360, duration: 35, delay: 4, opacity: 0.08 },
  ], []);

  return (
    <div className="relative" style={{ perspective: '1200px' }}>
      {/* Orbit rings */}
      {orbitRings.map((ring, i) => (
        <motion.div
          key={i}
          className="absolute left-1/2 top-1/2 rounded-full border"
          style={{
            width: ring.size,
            height: ring.size,
            marginLeft: -ring.size / 2,
            marginTop: -ring.size / 2,
            borderColor: `rgba(0, 212, 255, ${ring.opacity})`,
            transformStyle: 'preserve-3d',
          }}
          animate={{ rotateX: 75, rotateZ: 360 }}
          transition={{ duration: ring.duration, repeat: Infinity, ease: 'linear', delay: ring.delay }}
        />
      ))}

      <motion.div
        className="relative w-[260px] h-[260px]"
        animate={{ rotateY: 360 }}
        transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
        style={{ 
          transformStyle: 'preserve-3d',
          rotateX: useTransform(mouseY, [-0.5, 0.5], [15, -15]),
          rotateZ: useTransform(mouseX, [-0.5, 0.5], [-5, 5]),
        }}
      >
        {spheres.map((sphere, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: sphere.brightness, scale: 1 }}
            transition={{ delay: sphere.delay, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            style={{
              width: sphere.size,
              height: sphere.size,
              left: '50%',
              top: '50%',
              transform: `translate3d(${sphere.x}px, ${sphere.y}px, ${sphere.z}px) translate(-50%, -50%)`,
              background: `radial-gradient(circle at 30% 30%, ${sphere.color} 0%, transparent 70%)`,
              boxShadow: `0 0 ${sphere.size * 1.5}px ${sphere.color}60`,
            }}
          />
        ))}
        
        {/* Central core */}
        <motion.div 
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(0, 212, 255, 0.4) 0%, rgba(139, 92, 246, 0.2) 50%, transparent 100%)',
            boxShadow: '0 0 60px rgba(0, 212, 255, 0.4), 0 0 100px rgba(139, 92, 246, 0.2)',
          }}
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      </motion.div>
    </div>
  );
};

// Premium Parallax Background with enhanced effects
export const ParallaxBackground = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0.5, y: 0.5 });
  const { scrollYProgress } = useScroll();
  
  const smoothMouseX = useSpring(mousePosition.x, { stiffness: 30, damping: 20 });
  const smoothMouseY = useSpring(mousePosition.y, { stiffness: 30, damping: 20 });
  const smoothScroll = useSpring(scrollYProgress, { stiffness: 20, damping: 15 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) - 0.5,
        y: (e.clientY / window.innerHeight) - 0.5,
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const bgY1 = useTransform(smoothScroll, [0, 1], ['0%', '20%']);
  const bgY2 = useTransform(smoothScroll, [0, 1], ['0%', '12%']);
  const opacity = useTransform(smoothScroll, [0, 0.5], [1, 0.4]);

  const mouseX1 = useTransform(smoothMouseX, [-0.5, 0.5], [-20, 20]);
  const mouseY1 = useTransform(smoothMouseY, [-0.5, 0.5], [-20, 20]);

  return (
    <div className="fixed inset-0 z-[-1] overflow-hidden pointer-events-none">
      {/* Deep gradient base */}
      <div 
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse 150% 100% at 50% 0%, #0A1628 0%, #030810 40%, #010408 100%)
          `,
        }}
      />

      {/* Quantum Core Background */}
      <motion.div 
        style={{ opacity }}
        className="absolute inset-0"
      >
        <QuantumCore mouseX={smoothMouseX} mouseY={smoothMouseY} />
      </motion.div>

      {/* Animated gradient orbs */}
      <motion.div 
        style={{ x: mouseX1, y: mouseY1 }}
        className="absolute top-[-20%] right-[-15%] w-[70vw] h-[70vw] max-w-[900px] max-h-[900px]"
      >
        <motion.div 
          style={{ y: bgY1 }}
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.04, 0.08, 0.04],
          }}
          transition={{ duration: 8, repeat: Infinity }}
          className="w-full h-full rounded-full"
        >
          <div 
            className="w-full h-full rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(0, 212, 255, 0.15) 0%, rgba(139, 92, 246, 0.05) 40%, transparent 70%)',
              filter: 'blur(80px)',
            }}
          />
        </motion.div>
      </motion.div>

      {/* Secondary orb */}
      <motion.div 
        className="absolute top-[55%] left-[-15%] w-[50vw] h-[50vw] max-w-[600px] max-h-[600px]"
      >
        <motion.div 
          style={{ y: bgY2 }}
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.03, 0.06, 0.03],
          }}
          transition={{ duration: 10, repeat: Infinity, delay: 2 }}
          className="w-full h-full rounded-full"
        >
          <div 
            className="w-full h-full rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(139, 92, 246, 0.12) 0%, transparent 60%)',
              filter: 'blur(60px)',
            }}
          />
        </motion.div>
      </motion.div>

      {/* Subtle animated grid */}
      <motion.div 
        style={{ y: bgY2 }}
        className="absolute inset-0"
      >
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(0, 212, 255, 1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0, 212, 255, 1) 1px, transparent 1px)
            `,
            backgroundSize: '100px 100px',
          }}
        />
      </motion.div>

      {/* Vignette */}
      <div 
        className="absolute inset-0"
        style={{
          boxShadow: 'inset 0 0 300px 150px rgba(1, 4, 8, 0.95)',
        }}
      />
    </div>
  );
};

export default ParallaxBackground;
